import React, { useState } from "react";
import { ProductMetaDTO } from "Types/ProductMetaDTO";

interface ProductTableProps {
  products: ProductMetaDTO[];
  onSelectProduct: (id: number | null) => void;
  onDeleteProduct: (id: number) => void;
  onViewProduct: (id: number) => void;
  onEditProduct: (id: number) => void;
  onSelectProductReview: (id: number) => void;
  selectedProduct: number | null;
  isLoading?: boolean;
  isError?: boolean;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (size: number) => void;
  currentPage: number;
  itemsPerPage: number;
  selectedProducts?: number[];
  onSelectedProductsChange?: (selectedIds: number[]) => void;
  totalItems: number;
}

export const ProductTable: React.FC<ProductTableProps> = ({
  products,
  onSelectProduct,
  onDeleteProduct,
  onViewProduct,
  onEditProduct,
  onSelectProductReview,
  selectedProduct,
  isLoading = false,
  isError = false,
  onPageChange,
  onItemsPerPageChange,
  currentPage,
  itemsPerPage,
  totalItems,
  selectedProducts: externalSelectedProducts = [],
  onSelectedProductsChange,
}) => {
  const [selectAll, setSelectAll] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const triggerTransition = () => {
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    if (newSelectAll) {
      const currentPageProductIds = products.map((product) => product.product.id);

      onSelectedProductsChange?.(currentPageProductIds);

      if (currentPageProductIds.length > 0) {
        onSelectProduct(currentPageProductIds[0]);
      }
    } else {
      onSelectedProductsChange?.([]);
      onSelectProduct(null);
    }
  };

  const handleSelectProduct = (id: number, checked: boolean) => {
    let newSelectedProducts: number[];

    if (checked) {
      newSelectedProducts = [...externalSelectedProducts, id];
    } else {
      newSelectedProducts = externalSelectedProducts.filter(
        (productId: number) => productId !== id,
      );
    }

    onSelectedProductsChange?.(newSelectedProducts);

    if (newSelectedProducts.length > 0) {
      onSelectProduct(newSelectedProducts[0]);
    } else {
      onSelectProduct(null);
    }

    const currentPageProductIds = products.map((product) => product.product.id);

    setSelectAll(
      currentPageProductIds.every((id) => newSelectedProducts.includes(id)),
    );
  };

  return (
    <div className="font-body tracking-wider overflow-x-auto">
       { isError ? (
        <div className="py-10 text-center text-red-500">
          Error loading products
        </div>
      ) : (
        <>
          <div className="relative">
            {(isLoading || isTransitioning) && (
              <div className="absolute inset-0 bg-white/80 z-50 flex items-center justify-center min-h-[200px]">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin shadow-sm"></div>
                  <span className="text-sm font-semibold text-gray-700">Loading products...</span>
                </div>
              </div>
            )}
            <table className="min-w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="py-3 px-4 text-left left-0 bg-white">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500 whitespace-nowrap">
                    Product Name
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500">
                    Product Code
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500">
                    Category Name
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500">
                    Status
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-gray-500">
                      {!isLoading && !isTransitioning ? "No products found" : "\u00A0"}
                    </td>
                  </tr>
                ) : (
                  products.map((product) => (
                    <tr
                      key={product.product.id}
                      className={`border-t ${
                        selectedProduct === product.product.id
                          ? "bg-blue-50"
                          : ""
                      }`}
                      onClick={() =>
                        onSelectProduct(
                          selectedProduct === product.product.id
                            ? null
                            : product.product.id,
                        )
                      }
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={externalSelectedProducts.includes(
                            product.product.id,
                          )}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectProduct(
                              product.product.id,
                              e.target.checked,
                            );
                          }}
                        />
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {(product.product.itemDesc ?? "").length > 50
                          ? `${(product.product.itemDesc ?? "").substring(0, 50)}...`
                          : (product.product.itemDesc ?? "-")}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {product.product.item}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {product.product.categoryName || "-"}
                      </td>

                      <td className="py-3 px-4 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            product.product.isActive?.toString() === "Y"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {product.product.isActive?.toString() === "Y"
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </td>

                      <td className="py-3 px-4 flex gap-2">
                        <button
                          className="text-blue-500 hover:text-blue-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewProduct(product.product.id);
                          }}
                          title="View"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                        <button
                          className="text-green-500 hover:text-green-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditProduct(product.product.id);
                          }}
                          title="Edit"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                            <path d="m15 5 4 4" />
                          </svg>
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteProduct(product.product.id);
                          }}
                          title="Delete"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M3 6h18" />
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                            <line x1="10" x2="10" y1="11" y2="17" />
                            <line x1="14" x2="14" y1="11" y2="17" />
                          </svg>
                        </button>
                        <button
                          title="View Reviews"
                          className="text-purple-600 hover:text-purple-800 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectProductReview(product.product.id);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="hidden md:flex text-gray-600">
              Showing {startItem} to {endItem}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Show</span>
                <select
                  className="p-2 border rounded bg-white hover:bg-gray-50 cursor-pointer outline-none focus:border-orange-400 transition-colors"
                  value={itemsPerPage}
                  onChange={(e) => {
                    const newItemsPerPage = Number(e.target.value);
                    onItemsPerPageChange(newItemsPerPage);
                    onPageChange(1);
                    triggerTransition();
                  }}
                >
                  {[100, 500, 1000, 1500].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button
                  className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                  disabled={currentPage === 1 || isLoading || isTransitioning}
                  onClick={() => {
                    onPageChange(1);
                    triggerTransition();
                  }}
                  title="First Page"
                >
                  &lt;&lt;
                </button>
                <button
                  className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                  disabled={currentPage === 1 || isLoading || isTransitioning}
                  onClick={() => {
                    onPageChange(currentPage - 1);
                    triggerTransition();
                  }}
                  title="Previous Page"
                >
                  &lt;
                </button>
                <div className="flex items-center justify-center min-w-[40px] h-9 bg-gray-50 border rounded-md font-medium text-orange-600">
                  {currentPage}
                </div>
                <button
                  className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                  disabled={currentPage === totalPages || totalPages === 0 || isLoading || isTransitioning}
                  onClick={() => {
                    onPageChange(currentPage + 1);
                    triggerTransition();
                  }}
                  title="Next Page"
                >
                  &gt;
                </button>
                <button
                  className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                  disabled={currentPage === totalPages || totalPages === 0 || isLoading || isTransitioning}
                  onClick={() => {
                    onPageChange(totalPages);
                    triggerTransition();
                  }}
                  title="Last Page"
                >
                  &gt;&gt;
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
